import { FIELD_META, SECTION_OF_FIELD } from "@/features/profile/fieldMeta";
import { fieldLabel } from "@/features/profile/labels";
import { STEP_BY_FIELD } from "@/features/profile/wizard/steps";

/**
 * HR's ticks and crosses on a sign-up.
 *
 * HR marks each field right or wrong while checking it. A cross carries a
 * note, and is what the employee sees beside the field when the profile comes
 * back to them. A cross the employee has since changed stays a cross, marked
 * as changed, until HR looks again and ticks it.
 */
export type ReviewVerdict = "ok" | "issue";

export interface ReviewFlag {
  verdict: ReviewVerdict;
  note: string | null;
  /** Who marked it, and when. */
  by: string;
  at: string;
  /** The value it was marked against. */
  value: unknown;
  /** The employee changed a crossed field and sent the profile again. */
  changed_after?: boolean;
}

export type ReviewFlags = Record<string, ReviewFlag>;

/** What the review screen sends: a verdict and a note, or null to take it off. */
export type ReviewMark = { verdict: ReviewVerdict | null; note?: string | null } | null;

export const NOTE_LIMIT = 300;

export interface ReviewCounts {
  /** Ticked. */
  checked: number;
  /** Crossed and not yet changed by the employee. */
  toFix: number;
  /** Crossed, then changed by the employee: to be looked at again. */
  changed: number;
  /** Fields with any mark. */
  marked: number;
  /** Fields on the screen. */
  total: number;
}

/** How far through the fields HR is, counting only the fields on screen. */
export function reviewCounts(flags: ReviewFlags | undefined, keys: readonly string[]): ReviewCounts {
  let checked = 0;
  let toFix = 0;
  let changed = 0;
  for (const key of keys) {
    const flag = flags?.[key];
    if (!flag) continue;
    if (flag.verdict === "ok") checked += 1;
    else if (flag.changed_after) changed += 1;
    else toFix += 1;
  }
  return { checked, toFix, changed, marked: checked + toFix + changed, total: keys.length };
}

/** "12 checked, 2 to fix", as the review screen says it. */
export function countsText(counts: ReviewCounts): string {
  const parts = [`${counts.checked} checked`];
  if (counts.toFix) parts.push(`${counts.toFix} to fix`);
  if (counts.changed) parts.push(`${counts.changed} changed, to check again`);
  return parts.join(", ");
}

export interface CrossedField {
  field: string;
  label: string;
  note: string | null;
  /** The sign-up step to go to, to put it right. */
  stepKey: string;
  changed: boolean;
}

/** The crosses, in the order of the sign-up form. */
export function crossedFields(flags: ReviewFlags | undefined): CrossedField[] {
  if (!flags) return [];
  const order = Object.keys(FIELD_META);
  return Object.entries(flags)
    .filter(([, flag]) => flag?.verdict === "issue")
    .sort(([a], [b]) => rank(order, a) - rank(order, b))
    .map(([field, flag]) => ({
      field,
      label: fieldLabel(field),
      note: flag.note,
      stepKey: STEP_BY_FIELD[field] ?? SECTION_OF_FIELD[field] ?? "review",
      changed: Boolean(flag.changed_after),
    }));
}

function rank(order: string[], key: string): number {
  const index = order.indexOf(key);
  return index === -1 ? order.length : index;
}

/**
 * HR's note for a field the employee has to put right, to show under it.
 *
 * The days off are one answer in two columns, so a cross on either shows on
 * the question.
 */
export function hrNote(flags: ReviewFlags | undefined, field: string): string | undefined {
  const flag = flags?.[field] ?? (field === "days_off_rotate" ? flags?.weekly_off_days : undefined);
  if (!flag || flag.verdict !== "issue") return undefined;
  return `HR: ${flag.note ?? "Check this."}`;
}

/** The crosses on one sign-up step. */
export function crossedOnStep(flags: ReviewFlags | undefined, stepKey: string): CrossedField[] {
  return crossedFields(flags).filter((crossed) => crossed.stepKey === stepKey);
}
